/**
 * App 作用域 + 外观偏好全局态。
 *
 * 移植自原型 app.jsx 的 useTweaks（localStorage key 'na-tweaks'），改成
 * zustand + persist（key 仍为 'na-tweaks'，与原型存储兼容）。
 *
 * 持有两类状态：
 *  - appId：当前选中的 App 作用域（默认 'all'，即聚合视图），由顶栏 AppScopeSelect 切换。
 *  - 外观偏好 theme / density / accent / sidebarCollapsed：照原型 TWEAK_DEFAULTS。
 *
 * useApplyAppearance() 复刻原型 app.jsx 的 useEffect：把 resolvedTheme→data-theme、
 * density→data-density、accent→--accent-h 同步到 documentElement。theme==='system' 时
 * 跟随操作系统的 prefers-color-scheme（systemDark 实时跟踪 OS 偏好变化）。
 *
 * 注意：原型 useTweaks 里的 window.parent.postMessage 是 edit-mode 专用，此处不移植。
 */
import { useEffect, useState } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Density = 'compact' | 'standard' | 'comfortable';
export type TablePageSize = 18 | 30 | 50 | 100;

/** 主题偏好：浅色 / 深色 / 跟随系统（'system' 由 useApplyAppearance 解析为实际明暗）*/
export type ThemePref = 'light' | 'dark' | 'system';

export interface AppScopeState {
  /** 当前 App 作用域 id，'all' 为聚合视图 */
  appId: string;
  /** 主题偏好：浅色 / 深色 / 跟随系统 */
  theme: ThemePref;
  /** 密度：紧凑 / 标准 / 宽松 */
  density: Density;
  /** 强调色色相值（写入 --accent-h，无单位数字）*/
  accent: number;
  /** 侧边栏是否折叠 */
  sidebarCollapsed: boolean;
  /** 表格默认分页行数（本机偏好，不经后端） */
  tablePageSize: TablePageSize;
  /** 登录时默认打开的 App 作用域 id（'all' = 聚合视图）；本机偏好，登录完成时套用到 appId */
  defaultAppId: string;
  /** 登录时默认落地页路由（如 '/' / '/finance'）；本机偏好，from 无深链时跳转此页 */
  defaultPage: string;
  /** 切换当前 App 作用域 */
  setAppId: (id: string) => void;
  /** 局部更新本地偏好（theme / density / accent / sidebarCollapsed / tablePageSize / defaultAppId / defaultPage）*/
  setTweak: (
    patch: Partial<
      Pick<
        AppScopeState,
        | 'theme'
        | 'density'
        | 'accent'
        | 'sidebarCollapsed'
        | 'tablePageSize'
        | 'defaultAppId'
        | 'defaultPage'
      >
    >,
  ) => void;
}

// 默认值照原型 app.jsx 的 TWEAK_DEFAULTS（theme light / accent 50 / density compact /
// sidebarCollapsed false），外加 appId 默认 'all'。
export const useAppScope = create<AppScopeState>()(
  persist(
    (set) => ({
      appId: 'all',
      theme: 'light',
      density: 'compact',
      accent: 50,
      sidebarCollapsed: false,
      tablePageSize: 18,
      defaultAppId: 'all',
      defaultPage: '/',
      setAppId: (id) => set({ appId: id }),
      setTweak: (patch) => set(patch),
    }),
    { name: 'na-tweaks' },
  ),
);

/**
 * 登录完成时套用本机「默认 App / 默认页」偏好：把 defaultAppId 套到当前 appId，
 * 返回 defaultPage 路由（供 from 无深链时跳转）。在各登录完成点（Login/Verify2FA/BindTotp）调用。
 */
export function applyLoginDefaults(): string {
  const s = useAppScope.getState();
  s.setAppId(s.defaultAppId);
  return s.defaultPage;
}

/**
 * 跟踪操作系统 prefers-color-scheme 是否为 dark，并随 OS 偏好变化实时更新。
 * 复刻原型 app.jsx 的 systemDark useState + matchMedia change 监听（兼容旧版
 * addListener/removeListener 回退）。仅在 'system' 主题下用于解析实际明暗。
 */
function useSystemDark(): boolean {
  const [systemDark, setSystemDark] = useState(
    () => !!window.matchMedia?.('(prefers-color-scheme: dark)').matches,
  );

  useEffect(() => {
    if (!window.matchMedia) return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const on = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    // 新版浏览器用 addEventListener，旧版回退 addListener（Safari < 14）
    if (mql.addEventListener) mql.addEventListener('change', on);
    else mql.addListener(on);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', on);
      else mql.removeListener(on);
    };
  }, []);

  return systemDark;
}

/**
 * 把主题偏好解析为实际明暗：'system' → 跟随 OS（systemDark），否则原值。
 * 供 AppShell 顶栏图标 / TweaksPanel 联动复用。
 */
export function useResolvedTheme(): 'light' | 'dark' {
  const theme = useAppScope((s) => s.theme);
  const systemDark = useSystemDark();
  return theme === 'system' ? (systemDark ? 'dark' : 'light') : theme;
}

/**
 * 把外观偏好同步到 documentElement，复刻原型 app.jsx 的 useEffect：
 *   data-theme ← resolvedTheme（'system' 解析为实际明暗），
 *   data-density ← density，--accent-h ← accent。
 * 在 AppShell 顶层调用一次即可。
 */
export function useApplyAppearance(): void {
  const resolvedTheme = useResolvedTheme();
  const density = useAppScope((s) => s.density);
  const accent = useAppScope((s) => s.accent);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', resolvedTheme);
    document.documentElement.setAttribute('data-density', density);
    document.documentElement.style.setProperty('--accent-h', String(accent));
  }, [resolvedTheme, density, accent]);
}
